import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Tabs,
  Tab,
  Box,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Download,
  Refresh
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subMonths, startOfYear, endOfYear } from 'date-fns';

import CurrentVsHistoricalChart from './CurrentVsHistoricalChart';
import HistoricalComparisonChart from './HistoricalComparisonChart';
import { EnhancedAnalysisTab } from './EnhancedAnalysisTab';
import { MonthlyEnergySummary } from './MonthlyEnergySummary';
import { EnergyChart } from './EnergyChart';
import { reportingApi } from '../../services/reportingApi';

interface EnhancedReportingDashboardProps {
  siteId?: number;
  siteName?: string;
}

interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface EnhancedAnalysisData {
  start_date: string;
  end_date: string;
  total_days: number;
  compare_previous_year: boolean;
  include_trends: boolean;
  calculations: Array<{
    calculation_id: number;
    calculation_name: string;
    calculation_type: string;
    chart_type: string;
    tag_name: string;
    tag_description: string;
    tag_units: string;
    period_total: number;
    daily_data: Array<{
      date: string;
      energy: number | null;
      reset_detected: boolean;
      data_points: number;
      cumulative_energy?: number;
      original_daily?: number;
    }>;
    data_points: number;
    previous_year?: {
      period_total: number;
      daily_data: any[];
      data_points: number;
      change_percent: number;
    };
    trends?: {
      trend_direction: string;
      average: number;
      moving_average: Array<{
        date: string;
        moving_average: number;
      }>;
      min_value: number;
      max_value: number;
    };
  }>;
}

const EnhancedReportingDashboard: React.FC<EnhancedReportingDashboardProps> = ({
  siteId = 1,
  siteName = 'Cleardale Hydro'
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: subMonths(new Date(), 6),
    endDate: new Date()
  });
  const [analysisData, setAnalysisData] = useState<EnhancedAnalysisData | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [comparePreviousYear, setComparePreviousYear] = useState(false); // Disabled for performance
  const [includeTrends, setIncludeTrends] = useState(false); // Disabled for performance
  const [quickSelect, setQuickSelect] = useState('6months');

  const quickSelectOptions = [
    { value: '1month', label: 'Last Month', months: 1 },
    { value: '3months', label: 'Last 3 Months', months: 3 },
    { value: '6months', label: 'Last 6 Months', months: 6 },
    { value: '12months', label: 'Last 12 Months', months: 12 },
    { value: 'ytd', label: 'Year to Date', months: 0 },
    { value: 'lastyear', label: 'Last Full Year', months: 0 },
    { value: 'custom', label: 'Custom Range', months: 0 }
  ];

  const handleQuickSelect = (value: string) => {
    setQuickSelect(value);
    const now = new Date();
    
    switch (value) {
      case '1month':
      case '3months':
      case '6months':
      case '12months':
        const option = quickSelectOptions.find(opt => opt.value === value);
        if (option) {
          setDateRange({
            startDate: subMonths(now, option.months),
            endDate: now
          });
        }
        break;
      case 'ytd':
        setDateRange({
          startDate: startOfYear(now),
          endDate: now
        });
        break;
      case 'lastyear':
        const lastYear = new Date(now.getFullYear() - 1, 0, 1);
        setDateRange({
          startDate: startOfYear(lastYear),
          endDate: endOfYear(lastYear)
        });
        break;
    }
  };

  const fetchEnhancedAnalysis = async () => {
    setLoading(true);
    setError(null);

    try {
      const startDateStr = format(dateRange.startDate, 'yyyy-MM-dd');
      const endDateStr = format(dateRange.endDate, 'yyyy-MM-dd');
      
      const response = await reportingApi.getEnhancedEnergyAnalysis(
        startDateStr,
        endDateStr,
        {
          siteId,
          comparePreviousYear,
          includeTrends
        }
      );
      
      setAnalysisData(response);
    } catch (err) {
      setError(`Failed to load enhanced analysis: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnhancedAnalysis();
  }, [dateRange.startDate, dateRange.endDate, comparePreviousYear, includeTrends]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const exportData = () => {
    if (!analysisData) return;
    
    // Create CSV export of the analysis data
    const csvContent = analysisData.calculations.map(calc => {
      return calc.daily_data.map(day => [
        day.date,
        calc.calculation_name,
        day.energy || 'N/A',
        day.data_points,
        day.reset_detected ? 'Yes' : 'No'
      ].join(',')).join('\n');
    }).join('\n');
    
    const header = 'Date,Calculation,Energy (kWh),Data Points,Reset Detected\n';
    const blob = new Blob([header + csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `energy-analysis-${format(dateRange.startDate, 'yyyy-MM-dd')}-to-${format(dateRange.endDate, 'yyyy-MM-dd')}.csv`;
    a.click();
    
    window.URL.revokeObjectURL(url);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: '100%' }}>
        {/* Header Controls */}
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Typography variant="h5">
                Energy Analytics - {siteName}
              </Typography>
            }
            action={
              <Box display="flex" gap={1}>
                <Button
                  startIcon={<Refresh />}
                  onClick={fetchEnhancedAnalysis}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  startIcon={<Download />}
                  onClick={exportData}
                  disabled={!analysisData}
                >
                  Export
                </Button>
              </Box>
            }
          />
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={quickSelect === 'custom' ? 4 : 12}>
                <FormControl fullWidth>
                  <InputLabel>Quick Select</InputLabel>
                  <Select
                    value={quickSelect}
                    onChange={(e) => handleQuickSelect(e.target.value)}
                    label="Quick Select"
                  >
                    {quickSelectOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {quickSelect === 'custom' && (
                <>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      label="Start Date"
                      value={dateRange.startDate}
                      onChange={(date) => date && setDateRange(prev => ({ ...prev, startDate: date }))}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      label="End Date"
                      value={dateRange.endDate}
                      onChange={(date) => date && setDateRange(prev => ({ ...prev, endDate: date }))}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                </>
              )}
              
              {/* Removed Previous Year and Trends toggles for better performance and simpler UX */}
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {/* Analysis Summary */}
            {analysisData && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">Analysis Period</Typography>
                  <Typography variant="h6">
                    {analysisData?.total_days || 0} days
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">Calculations</Typography>
                  <Typography variant="h6">
                    {analysisData?.calculations?.length || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Data Source</Typography>
                  <Typography variant="body1">
                    Intelligent prioritization: Meter → SCADA → Historical
                  </Typography>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Main Content Tabs */}
        <Card>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Current vs Historical" />
            <Tab label="Year Comparison" />
            <Tab label="Enhanced Analysis" />
            <Tab label="Monthly Summary" />
          </Tabs>

          <CardContent>
            {loading && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  Loading enhanced analytics...
                </Typography>
              </Box>
            )}

            {!loading && (
              <>
                {/* Tab Panels */}
                {activeTab === 0 && (
                  <CurrentVsHistoricalChart
                    siteId={siteId}
                    siteName={siteName}
                  />
                )}

                {activeTab === 1 && (
                  <HistoricalComparisonChart
                    siteId={siteId}
                    siteName={siteName}
                  />
                )}

                {activeTab === 2 && analysisData && (
                  <EnhancedAnalysisTab
                    analysisData={{
                      site_id: siteId,
                      site_name: siteName,
                      analysis_period: {
                        start_date: analysisData.start_date,
                        end_date: analysisData.end_date,
                        days_analyzed: analysisData.total_days
                      },
                      calculations: analysisData.calculations.map(calc => ({
                        ...calc,
                        calculation_id: calc.calculation_id.toString()
                      }))
                    }}
                    loading={loading}
                    error={error}
                  />
                )}

                {/* Removed unused previous year and trends display section */}

                {activeTab === 3 && (
                  <MonthlyEnergySummary />
                )}

              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default EnhancedReportingDashboard;
